import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import L from 'leaflet';
import type { Vendor, Position } from '../../../types';
import type { MarkerType } from '../markerSvg';
import type { EditorMode } from '../../../context/EditorModeContext.utils';
import { MAP_CONFIG } from '../mapConfig';
import { attachTapDetector } from '../../../lib/map/gestures';
import {
  clampScale,
  hitAreaSizeFor,
  LABEL_MIN_ZOOM,
  createVendorMarkerEl,
  updateVendorMarkerEl,
  createPlaceholderMarkerEl,
  createPreviewMarkerEl,
  setPlaceholderMarkerClass,
} from './markerDom';

interface Options {
  map: L.Map;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  markerElsRef: MutableRefObject<Map<string, HTMLElement>>;
  placeholderElsRef: MutableRefObject<Map<string, HTMLElement>>;
  markerLabelElsRef: MutableRefObject<Map<string, HTMLElement>>;
  placeholderLabelElsRef: MutableRefObject<Map<string, HTMLElement>>;
  positionUidToElRef: MutableRefObject<Map<string, HTMLElement>>;
  previewElRef: MutableRefObject<HTMLElement | null>;
  filteredVendors: Vendor[];
  positions: Position[];
  vendors: Vendor[];
  vendorByPositionUid: Map<string, Vendor>;
  positionByUid: Map<string, Position>;
  isEditing: boolean;
  mode: EditorMode;
  getSvg: (type: MarkerType, categoryId: string, categoryColor: string) => string;
  getCategoryColor: (categoryId: string) => string;
  /** Resolves a position's location tag (street name) for the marker aria-label. */
  nameFor: (positionId: string) => string | undefined;
  onVendorTapRef: MutableRefObject<((uid: string) => void) | undefined>;
  attachDragOrSelect: (
    el: HTMLElement,
    uid: string,
    getCurrentCoords: () => [number, number],
  ) => void;
}

export function useMarkerSync({
  map,
  containerRef,
  markerElsRef,
  placeholderElsRef,
  markerLabelElsRef,
  placeholderLabelElsRef,
  positionUidToElRef,
  previewElRef,
  filteredVendors,
  positions,
  vendors,
  vendorByPositionUid,
  positionByUid,
  isEditing,
  mode,
  getSvg,
  getCategoryColor,
  nameFor,
  onVendorTapRef,
  attachDragOrSelect,
}: Options) {
  // ----- updateMarkerPositions: declared FIRST so the sync effects below
  // can reference it without tripping use-before-declared.
  const updateMarkerPositions = useCallback(() => {
    const zoom = map.getZoom();
    const scale = clampScale(zoom);
    const showLabels = zoom >= LABEL_MIN_ZOOM;

    // One write sizes every marker's invisible hit area alongside the
    // transform (see .markerHitArea in ScaledMarkerLayer.module.css).
    containerRef.current?.style.setProperty('--marker-hit-size', `${hitAreaSizeFor(zoom)}px`);

    markerElsRef.current.forEach((el, posUid) => {
      if (el.dataset.dragging === 'true') return;
      const vendor = vendorByPositionUid.get(posUid);
      if (!vendor) return;
      const point = map.latLngToLayerPoint(L.latLng(vendor.coordinates[0], vendor.coordinates[1]));
      el.style.transform = `translate(${point.x}px, ${point.y}px) translate(-50%,-50%) scale(${scale})`;
      el.style.zIndex = String(MAP_CONFIG.height - Math.round(vendor.coordinates[0]));
      const labelEl = markerLabelElsRef.current.get(posUid);
      if (labelEl) labelEl.style.visibility = showLabels ? '' : 'hidden';
    });

    placeholderElsRef.current.forEach((el, uid) => {
      if (el.dataset.dragging === 'true') return;
      const pos = positionByUid.get(uid);
      if (!pos) return;
      const point = map.latLngToLayerPoint(L.latLng(pos.coordinates[0], pos.coordinates[1]));
      el.style.transform = `translate(${point.x}px, ${point.y}px) translate(-50%,-50%) scale(${scale})`;
      el.style.zIndex = String(MAP_CONFIG.height - Math.round(pos.coordinates[0]));
      const labelEl = placeholderLabelElsRef.current.get(uid);
      if (labelEl) labelEl.style.visibility = showLabels ? '' : 'hidden';
    });

    const previewEl = previewElRef.current;
    if (previewEl && mode.kind === 'placement-awaiting-details') {
      const { coords } = mode;
      const point = map.latLngToLayerPoint(L.latLng(coords[0], coords[1]));
      previewEl.style.transform = `translate(${point.x}px, ${point.y}px) translate(-50%,-50%) scale(${scale})`;
    }
    // Refs (markerElsRef, placeholderElsRef, etc.) are stable — intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, vendorByPositionUid, positionByUid, mode]);

  // Rebuild vendor markers whenever isEditing flips so drag/select handlers
  // get (re-)attached on the current set of markers.
  useEffect(() => {
    markerElsRef.current.forEach((el) => {
      const posUid = el.dataset.positionUid;
      if (posUid) positionUidToElRef.current.delete(posUid);
      el.remove();
    });
    markerElsRef.current.clear();
    markerLabelElsRef.current.clear();
    // Refs are stable — intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // rAF handle for the chunked marker-creation loop (sync effect below).
  const chunkRafRef = useRef<number | null>(null);
  // Bumped when chunked creation finishes so consumers (selection/ping effects
  // in index.tsx) re-run once the async-created markers are actually in the DOM.
  // The synchronous/edit path doesn't need it — those markers exist before
  // those effects run, same as before chunking.
  const [markersSettledTick, setMarkersSettledTick] = useState(0);

  // Sync markers when filteredVendors or positions change.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Cancel any chunked creation still in flight from a previous run.
    if (chunkRafRef.current !== null) {
      cancelAnimationFrame(chunkRafRef.current);
      chunkRafRef.current = null;
    }

    const currentPositionUids = new Set(filteredVendors.map((v) => v.positionUid));
    markerElsRef.current.forEach((el, posUid) => {
      if (!currentPositionUids.has(posUid)) {
        positionUidToElRef.current.delete(posUid);
        el.remove();
        markerElsRef.current.delete(posUid);
        markerLabelElsRef.current.delete(posUid);
      }
    });

    // Creates one vendor's marker element (the expensive bit — createElement +
    // SVG parse) and registers it in the lookup maps.
    const createMarker = (vendor: Vendor) => {
      const svg = getSvg(vendor.type, vendor.categoryId, getCategoryColor(vendor.categoryId));
      const { el, labelEl } = createVendorMarkerEl(vendor, isEditing, svg, nameFor(vendor.positionId));

      // Tap on a marker → notify the parent via callback prop.
      const vendorUidSnapshot = vendor.uid;
      attachTapDetector(el, () => {
        if (isEditing) return;
        onVendorTapRef.current?.(vendorUidSnapshot);
      });

      // Keyboard activation: Enter/Space routes to the same onVendorTap path
      // as a pointer tap. Gated at fire time on role="button" so the handler
      // always matches the a11y state applyMarkerA11y last applied to this
      // element — a reused element can flip between vendor and amenity.
      el.addEventListener('keydown', (e: KeyboardEvent) => {
        if (el.getAttribute('role') !== 'button') return;
        if (isEditing) return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          // Prevent Space from scrolling the page / Leaflet from swallowing it.
          e.preventDefault();
          onVendorTapRef.current?.(vendorUidSnapshot);
        }
      });

      if (isEditing) {
        el.style.cursor = 'move';
        attachDragOrSelect(el, vendor.positionUid, () => {
          const v = vendorByPositionUid.get(vendor.positionUid);
          return v ? ([...v.coordinates] as [number, number]) : [...vendor.coordinates];
        });
      }

      container.appendChild(el);
      markerElsRef.current.set(vendor.positionUid, el);
      markerLabelElsRef.current.set(vendor.positionUid, labelEl);
      // A position can appear in either the vendor map or the placeholder map,
      // never both (placeholders filter to unassigned positions). Overwriting
      // here is safe and ensures the index always points to the visible element.
      positionUidToElRef.current.set(vendor.positionUid, el);
    };

    // Update existing markers in place (cheap); collect ones needing a brand-new
    // element so we can build them off the critical path.
    const toCreate: Vendor[] = [];
    filteredVendors.forEach((vendor) => {
      const existing = markerElsRef.current.get(vendor.positionUid);
      if (existing) {
        const svg = getSvg(vendor.type, vendor.categoryId, getCategoryColor(vendor.categoryId));
        updateVendorMarkerEl(
          existing,
          vendor,
          markerLabelElsRef.current.get(vendor.positionUid),
          isEditing,
          svg,
          nameFor(vendor.positionId),
        );
      } else {
        toCreate.push(vendor);
      }
    });

    // Edit mode: build synchronously — drag/select correctness matters more
    // than paint timing, and the editor isn't perf-measured.
    if (isEditing || toCreate.length === 0) {
      toCreate.forEach(createMarker);
      updateMarkerPositions();
      return;
    }

    // Otherwise build in time-boxed chunks across animation frames. Creating a
    // few-hundred markers in one synchronous pass was a ~1.8s main-thread task
    // that blocked the map-image paint (LCP) and dominated TBT; spreading it
    // keeps every task short (the map paints first, pins fill in over the next
    // few frames) so the marker work no longer counts toward TBT.
    let i = 0;
    const renderChunk = () => {
      chunkRafRef.current = null;
      const start = performance.now();
      while (i < toCreate.length && performance.now() - start < 12) {
        createMarker(toCreate[i]!);
        i += 1;
      }
      updateMarkerPositions();
      if (i < toCreate.length) {
        chunkRafRef.current = requestAnimationFrame(renderChunk);
      } else {
        // All async-created markers are now in the DOM — let selection/ping
        // effects re-run so a deep-linked vendor gets highlighted/pinged.
        setMarkersSettledTick((t) => t + 1);
      }
    };
    chunkRafRef.current = requestAnimationFrame(renderChunk);

    return () => {
      if (chunkRafRef.current !== null) {
        cancelAnimationFrame(chunkRafRef.current);
        chunkRafRef.current = null;
      }
    };
    // Refs are stable — intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredVendors,
    isEditing,
    map,
    getSvg,
    getCategoryColor,
    nameFor,
    vendorByPositionUid,
    attachDragOrSelect,
    updateMarkerPositions,
  ]);

  // Transient preview marker while the Add Position form is being filled.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (mode.kind !== 'placement-awaiting-details') {
      previewElRef.current?.remove();
      previewElRef.current = null;
      return;
    }
    const { coords } = mode;
    let el = previewElRef.current;
    if (!el) {
      el = createPreviewMarkerEl();
      container.appendChild(el);
      previewElRef.current = el;
    }
    const zoom = map.getZoom();
    const scale = clampScale(zoom);
    const point = map.latLngToLayerPoint(L.latLng(coords[0], coords[1]));
    // eslint-disable-next-line react-hooks/immutability -- ok: imperative DOM update inside an effect, ref is the escape hatch
    el.style.transform = `translate(${point.x}px, ${point.y}px) translate(-50%,-50%) scale(${scale})`;
    el.style.zIndex = String(MAP_CONFIG.height - Math.round(coords[0]) + 10000);
    // Refs are stable — intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, map]);

  // Placeholder markers for unassigned positions (edit mode only).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!isEditing) {
      placeholderElsRef.current.forEach((el, uid) => {
        positionUidToElRef.current.delete(uid);
        el.remove();
      });
      placeholderElsRef.current.clear();
      placeholderLabelElsRef.current.clear();
      return;
    }

    // Amenity-typed positions get a synthetic vendor (`utils/amenities`),
    // so they're matched by the `vendors.some` check and excluded here.
    const unassigned = positions.filter((p) => !vendors.some((v) => v.positionUid === p.uid));
    const currentUids = new Set(unassigned.map((p) => p.uid));
    placeholderElsRef.current.forEach((el, uid) => {
      if (!currentUids.has(uid)) {
        positionUidToElRef.current.delete(uid);
        el.remove();
        placeholderElsRef.current.delete(uid);
        placeholderLabelElsRef.current.delete(uid);
      }
    });

    unassigned.forEach((pos) => {
      let el = placeholderElsRef.current.get(pos.uid);
      if (!el) {
        const { el: newEl, labelEl } = createPlaceholderMarkerEl(pos);
        el = newEl;
        const uid = pos.uid;
        attachDragOrSelect(el, uid, () => {
          const current = positionByUid.get(uid);
          return current ? ([...current.coordinates] as [number, number]) : [...pos.coordinates];
        });
        container.appendChild(el);
        placeholderElsRef.current.set(pos.uid, el);
        placeholderLabelElsRef.current.set(pos.uid, labelEl);
        positionUidToElRef.current.set(pos.uid, el);
      }
      setPlaceholderMarkerClass(el, pos.labelPosition);
      const labelEl = placeholderLabelElsRef.current.get(pos.uid);
      if (labelEl) labelEl.textContent = pos.positionId;
    });

    updateMarkerPositions();
    // Refs are stable — intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, vendors, isEditing, attachDragOrSelect, positionByUid, updateMarkerPositions]);

  // Throttle continuous `move` events to one update per animation frame.
  // During a mobile pinch-zoom Leaflet fires `move` many times per frame;
  // running the full marker-position loop each time causes visible stutter.
  // `moveend` and `viewreset` are infrequent and bypass the throttle.
  const rafIdRef = useRef<number | null>(null);
  const updateMarkerPositionsThrottled = useCallback(() => {
    if (rafIdRef.current !== null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      updateMarkerPositions();
    });
  }, [updateMarkerPositions]);

  return { updateMarkerPositions, updateMarkerPositionsThrottled, rafIdRef, markersSettledTick };
}
